import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useMemo } from 'react';
import SortOption from '~/constants/common/SortOption';
import { useIsTradeInApplied } from '~/context/common/TradeContext';
import { useUserLocation } from '~/context/common/UserContext';
import VehicleContext, {
  useGetVehicleDetails
} from '~/context/vdp/VehicleContext';
import type {
  InventoryRequestAnalytics,
  InventoryRequest
} from '~/contracts/common';
import type { RequestedFeature } from '~/contracts/common';
import fetchFacetData from '~/services/common/fetchFacetData';
import BrowserCookies from '~/utilities/BrowserCookies';
import { generateErrorLogger } from '~/utilities/loggers';

const dummyAnalyticsData: InventoryRequestAnalytics = {
  browser: '',
  clientId: 'vdp-ui',
  deviceName: '',
  isBot: false,
  isFirstActiveSearchSession: false,
  isMobileDevice: false,
  previousSearchRequestId: '',
  referrer: '',
  searchRequestId: '',
  searchSessionId: '',
  utmParams: {}
};

const baseRequestedFeatures: RequestedFeature[] = [
  'HideImpossibleCombos',
  'LocationBasedPrefiltering',
  'LoanTermPricing'
];

export default function useGetSimilarVehicleCount() {
  const {
    similarVehicleCount: { setData, setLoading, setError }
  } = useContext(VehicleContext);
  const { make = '', parentModel = '' } = useGetVehicleDetails();
  const { zip = '' } = useUserLocation();
  const isTradeInApplied = useIsTradeInApplied();
  const conditionalRequestedFeatures = useMemo<RequestedFeature[]>(
    () => [isTradeInApplied ? 'ApplyTradeIn' : 'FetchTradeIn'],
    [isTradeInApplied]
  );

  const requestedFeatures = useMemo<RequestedFeature[]>(() => {
    return baseRequestedFeatures.concat(conditionalRequestedFeatures);
  }, [conditionalRequestedFeatures]);

  const request = useMemo<InventoryRequest>(
    () => ({
      analyticsData: dummyAnalyticsData,
      browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
      filters: {
        makes: [{ name: make, parentModels: [{ name: parentModel }] }]
      },
      pagination: { page: 1, pageSize: 24 },
      requestedFeatures,
      sortBy: SortOption.MostPopular,
      zip5: zip
    }),
    [make, parentModel, requestedFeatures, zip]
  );

  useEffectWithController(
    controller => {
      setLoading();

      fetchFacetData(
        {
          ...request
        },
        controller
      )
        .then(({ data: { pagination } }) => {
          const totalCount = Math.max(0, pagination.totalMatchedInventory - 1);
          setData(totalCount);
        })
        .catch(
          generateErrorLogger(
            'Get Inventory Count',
            {},
            { callback: setError, controller }
          )
        );
    },
    [request, setData, setError, setLoading]
  );
}
